import { useState } from "react"
import { toast } from "react-toastify"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import styled, { css } from "styled-components"

import { useReplaceWHManagerOrderItemMutation } from "store/api/warehouseManagerApi"
import { ReactComponent as IconClose } from "assets/svg/icon-close-small.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ButtonNew } from "components/form/ButtonNew"
import { InputNew } from "components/form/InputNew"

const TableRow = styled.div<{ canReplace?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  ${({ canReplace }) =>
    canReplace &&
    `
    &:hover {
      background-color: #F8FAFC;
      
      [data-action-button] {
        opacity: 1;
      }
    }
  `}
`

const TableCell = styled.div`
  display: flex;
  align-items: center;
`

const SerialNumberWithActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const SerialNumberInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const SerialNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const ActionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 8px;
  margin-left: auto;
  align-self: flex-start;
  margin-top: 5px;
`

const SerialNumberInput = styled(InputNew)`
  flex: 1;
  margin: 0;

  input {
    padding: 6px 12px;
    font-size: 14px;
  }
`

const ActionButton = styled(ButtonNew)<{ isDisabled?: boolean }>`
  font-size: 12px;
  height: 32px;
  padding: 0 12px;

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: #cbd5e1 !important;
      background: #f1f5f9 !important;
      border-color: #f1f5f9 !important;
      pointer-events: none;
    `}
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: #94a3b8;
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const ReplaceButtonWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: auto;
  margin-right: 8px;
`

interface OrderItemRowProps {
  index: number
  serialNumber: string
  canReplace: boolean
  orderNumber: number
  onReplaceSuccess: (oldSN: string, newSN: string) => void
}

export const OrderItemRow = ({ index, serialNumber, canReplace, orderNumber, onReplaceSuccess }: OrderItemRowProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedValue, setEditedValue] = useState(serialNumber)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [replaceWHManagerOrderItem, { isLoading: isReplacing }] = useReplaceWHManagerOrderItemMutation()

  const handleReplaceClick = () => {
    setIsEditing(true)
    setErrorMessage(undefined)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedValue(serialNumber)
    setErrorMessage(undefined)
  }

  const handleValueChange = (value: string) => {
    setEditedValue(value)
    setErrorMessage(undefined)
  }

  const handleSave = async () => {
    const trimmedValue = editedValue.trim()

    if (!trimmedValue || trimmedValue === serialNumber) {
      handleCancel()
      return
    }

    try {
      await replaceWHManagerOrderItem({
        id: orderNumber,
        oldSN: serialNumber,
        newSN: trimmedValue,
      }).unwrap()

      toast.success("Serial number replaced successfully!")
      onReplaceSuccess(serialNumber, trimmedValue)
      setIsEditing(false)
      setErrorMessage(undefined)
    } catch (error) {
      const errorMsg = getErrorMessage(error as FetchBaseQueryError | SerializedError | undefined)
      setErrorMessage(errorMsg)
    }
  }

  return (
    <TableRow canReplace={canReplace}>
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        {canReplace && isEditing ? (
          <SerialNumberWithActions>
            <SerialNumberInputWrapper>
              <SerialNumberInput
                value={editedValue}
                onChange={e => handleValueChange(e.currentTarget.value)}
                errorMessage={errorMessage}
                autoFocus
              />
              <ActionButtonsWrapper>
                <ActionButton onClick={handleSave} color="greyBordered" isDisabled={isReplacing || !!errorMessage}>
                  Save
                </ActionButton>
                <CloseButton onClick={handleCancel} disabled={isReplacing}>
                  <IconClose />
                </CloseButton>
              </ActionButtonsWrapper>
            </SerialNumberInputWrapper>
          </SerialNumberWithActions>
        ) : canReplace ? (
          <SerialNumberRow>
            <span>{serialNumber}</span>
            <ReplaceButtonWrapper data-action-button>
              <ActionButton onClick={handleReplaceClick} color="greyBordered">
                Replace
              </ActionButton>
            </ReplaceButtonWrapper>
          </SerialNumberRow>
        ) : (
          serialNumber
        )}
      </TableCell>
    </TableRow>
  )
}
